import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { bumpEngagement } from "@/lib/engagement";
import { TrendingUp } from "lucide-react";

const STORAGE_KEY = "titans_confidence_vote_v1";

const BUCKETS = [
  { label: "0–20%", emoji: "😡", color: "bg-titans-red", textCls: "text-titans-red" },
  { label: "21–40%", emoji: "😕", color: "bg-orange-500", textCls: "text-orange-400" },
  { label: "41–60%", emoji: "😐", color: "bg-yellow-500", textCls: "text-yellow-400" },
  { label: "61–80%", emoji: "🙂", color: "bg-titans-blue", textCls: "text-titans-blue" },
  { label: "81–100%", emoji: "😎", color: "bg-green-500", textCls: "text-green-400" },
];

function bucketFor(v) {
  if (v <= 20) return 0;
  if (v <= 40) return 1;
  if (v <= 60) return 2;
  if (v <= 80) return 3;
  return 4;
}

export default function ConfidenceMeter() {
  const [value, setValue] = useState(50);
  const [submitting, setSubmitting] = useState(false);
  const [aggregate, setAggregate] = useState({ count: 0, average: 0, buckets: [0, 0, 0, 0, 0] });
  const [lastVote, setLastVote] = useState(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? parseInt(raw, 10) : null;
  });
  const [history, setHistory] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/confidence");
      setAggregate(data);
    } catch (e) {
      console.error("ConfidenceMeter load failed:", e);
    }
    api.get("/confidence/history").then((r) => setHistory(r.data.days || [])).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/confidence", { value });
      localStorage.setItem(STORAGE_KEY, String(value));
      setLastVote(value);
      toast.success("Confidence locked in.");
      bumpEngagement("widget", { dedupeKey: "confidence-cast" });
      load();
    } catch {
      toast.error("Couldn't submit vote");
    } finally {
      setSubmitting(false);
    }
  };

  const activeBucket = bucketFor(value);
  const activeMeta = BUCKETS[activeBucket];
  const total = aggregate.buckets.reduce((s, n) => s + n, 0) || 1;
  const avgBucket = bucketFor(Math.round(aggregate.average));

  return (
    <div data-testid="confidence-meter" className="titans-card p-6 md:p-8">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="font-cond uppercase tracking-[0.25em] text-[10px] text-titans-blue inline-flex items-center gap-1">
            <TrendingUp size={12} /> Fan Confidence
          </div>
          <h3 className="font-display text-2xl md:text-3xl uppercase tracking-wide mt-2 text-white">
            How confident are you in the Titans?
          </h3>
        </div>
        <div className="text-right">
          <div className="text-titans-silver text-[10px] font-cond uppercase tracking-wider">League Avg</div>
          <div className="font-display text-3xl text-white leading-none">{aggregate.average}%</div>
          <div className="text-titans-silver text-[10px] font-cond uppercase tracking-wider mt-1">{aggregate.count} votes</div>
        </div>
      </div>

      {/* SLIDER */}
      <div className="grid md:grid-cols-[1fr_180px] gap-6 items-center">
        <div>
          <input
            type="range"
            min="0"
            max="100"
            step="1"
            value={value}
            onChange={(e) => setValue(parseInt(e.target.value, 10))}
            data-testid="confidence-slider"
            className="w-full accent-titans-red cursor-pointer"
            style={{
              background: `linear-gradient(90deg,
                #C8102E 0%, #C8102E 20%,
                #f97316 20%, #f97316 40%,
                #eab308 40%, #eab308 60%,
                #4B92DB 60%, #4B92DB 80%,
                #22c55e 80%, #22c55e 100%)`,
              height: "10px",
              borderRadius: 0,
              outline: "none",
              WebkitAppearance: "none",
            }}
          />
          <div className="mt-3 grid grid-cols-5 gap-1 text-center">
            {BUCKETS.map((b, i) => (
              <button
                key={b.label}
                type="button"
                onClick={() => setValue([10, 30, 50, 70, 95][i])}
                data-testid={`confidence-bucket-${i}`}
                className={`py-2 transition-colors ${
                  activeBucket === i ? `${b.color} text-white` : "bg-titans-void/60 text-titans-silver hover:text-white"
                }`}
              >
                <div className="text-lg">{b.emoji}</div>
                <div className="font-cond uppercase tracking-wider text-[9px] leading-none mt-1">{b.label}</div>
              </button>
            ))}
          </div>
        </div>
        <div className="text-center">
          <div className="text-7xl leading-none">{activeMeta.emoji}</div>
          <div className={`font-display text-5xl mt-2 ${activeMeta.textCls}`}>{value}%</div>
          <button
            onClick={submit}
            disabled={submitting}
            data-testid="confidence-submit"
            className="mt-3 w-full bg-titans-red hover:bg-titans-red-hover font-cond uppercase tracking-wider text-xs px-4 py-2.5 disabled:opacity-50"
          >
            {lastVote != null ? "Update Vote" : "Lock In"}
          </button>
          {lastVote != null && (
            <div className="text-titans-silver text-[10px] font-cond uppercase tracking-wider mt-2">
              Your last: {lastVote}%
            </div>
          )}
        </div>
      </div>

      {/* DISTRIBUTION */}
      <div className="mt-8">
        <div className="font-cond uppercase tracking-[0.25em] text-[10px] text-titans-silver mb-3">Fanbase Pulse</div>
        <div className="space-y-2">
          {BUCKETS.map((b, i) => {
            const n = aggregate.buckets[i] || 0;
            const pct = Math.round((n / total) * 100);
            const isLeader = aggregate.count > 0 && i === avgBucket;
            return (
              <div key={b.label} data-testid={`confidence-dist-${i}`} className="flex items-center gap-3">
                <div className="w-20 shrink-0 flex items-center gap-2 text-sm">
                  <span className="text-lg">{b.emoji}</span>
                  <span className="font-cond uppercase tracking-wider text-[10px] text-titans-silver">{b.label}</span>
                </div>
                <div className="flex-1 bg-titans-void/60 h-4 relative overflow-hidden">
                  <div
                    className={`absolute inset-y-0 left-0 ${b.color} transition-[width] duration-500`}
                    style={{ width: aggregate.count ? `${pct}%` : "0%" }}
                  />
                  {isLeader && (
                    <span className="absolute right-1 top-1/2 -translate-y-1/2 text-[9px] font-cond uppercase tracking-wider text-white">
                      Avg
                    </span>
                  )}
                </div>
                <div className="w-14 text-right font-display text-sm text-white">{n}</div>
              </div>
            );
          })}
        </div>
      </div>
      {/* 30-DAY TREND */}
      <div className="mt-8" data-testid="confidence-trend">
        <div className="font-cond uppercase tracking-[0.25em] text-[10px] text-titans-silver mb-3">30-Day Trend</div>
        <TrendChart days={history} />
      </div>
    </div>
  );
}

function TrendChart({ days }) {
  if (!days || days.length < 2) {
    return (
      <p className="text-titans-silver text-sm font-body" data-testid="confidence-trend-empty">
        The trend line appears once fans have voted on at least two different days. Lock in your vote to get it started.
      </p>
    );
  }
  const W = 600, H = 150, PAD = 26;
  const xs = days.map((_, i) => PAD + (i * (W - PAD * 2)) / (days.length - 1));
  const ys = days.map((d) => H - PAD - ((d.average / 100) * (H - PAD * 2)));
  const line = xs.map((x, i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${ys[i].toFixed(1)}`).join(" ");
  const area = `${line} L${xs[xs.length - 1].toFixed(1)},${H - PAD} L${xs[0].toFixed(1)},${H - PAD} Z`;
  const latest = days[days.length - 1];
  const first = days[0];
  const delta = Math.round(latest.average - first.average);
  return (
    <div>
      <div className="flex items-center gap-3 mb-2">
        <span className="font-display text-2xl text-white leading-none">{Math.round(latest.average)}%</span>
        <span className={`font-cond uppercase tracking-wider text-[10px] px-1.5 py-0.5 rounded ${delta >= 0 ? "bg-emerald-500/15 text-emerald-500" : "bg-titans-red/15 text-titans-red"}`}>
          {delta >= 0 ? "▲" : "▼"} {Math.abs(delta)} pts over {days.length} day{days.length === 1 ? "" : "s"}
        </span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label="Fan confidence 30 day trend">
        <defs>
          <linearGradient id="conf-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#4B92DB" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#4B92DB" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {[25, 50, 75].map((g) => {
          const gy = H - PAD - ((g / 100) * (H - PAD * 2));
          return (
            <g key={g}>
              <line x1={PAD} x2={W - PAD} y1={gy} y2={gy} stroke="rgba(15,23,42,0.10)" strokeDasharray="3 5" />
              <text x={4} y={gy + 3} fontSize="9" fill="#7C8A99" fontFamily="Barlow Condensed">{g}</text>
            </g>
          );
        })}
        <path d={area} fill="url(#conf-fill)" />
        <path d={line} fill="none" stroke="#2E6FB4" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        {xs.map((x, i) => (
          <circle key={i} cx={x} cy={ys[i]} r={i === xs.length - 1 ? 4 : 2.5} fill={i === xs.length - 1 ? "#C8102E" : "#2E6FB4"}>
            <title>{`${days[i].date}: ${days[i].average}% (${days[i].count} votes)`}</title>
          </circle>
        ))}
        <text x={PAD} y={H - 6} fontSize="9" fill="#7C8A99" fontFamily="Barlow Condensed">{first.date.slice(5)}</text>
        <text x={W - PAD} y={H - 6} fontSize="9" fill="#7C8A99" textAnchor="end" fontFamily="Barlow Condensed">{latest.date.slice(5)}</text>
      </svg>
    </div>
  );
}
